// /files: the files I put on Share and those shared with me (RFC-054 "Web",
// byj-1wv.70.4), as /shares' rows (byj-1wv.51.17): mine, the ones I handed
// in under a class's assignment — which go with the class's retention — and
// those shared with me, each a row with its ⋯, as /templates lists templates
// (byj-1wv.51.14). The head says what mine take of my quota, opens Upload a
// file — a dialog with a drop zone, the computer's chooser inside it — and
// what a terminal does with files: a folder is put from one, which packs it
// and leaves out what is never sent. A file dropped anywhere on the page is
// put, then opened. With none of mine besides hand-ins, My files is the
// dialog's own drop zone, lit while a file is dragged over the page
// (byj-1wv.51.25). One file is put at a time (byj-1wv.70.8).
import { useCallback, useEffect, useRef, useState, type DragEvent, type RefObject } from "react";
import { api, fileContentURL, type ShareFile } from "../api";
import { type FilesApi, fileBytes, putCommand, putNewFile } from "../files";
import { useConfirm } from "../components/Confirm";
import { FileDescribe } from "../components/FileDescribe";
import { FileRow } from "../components/FileRow";
import { Meter, nearlyFull } from "../components/Meter";
import { useModalKeys } from "../components/Modal";
import { Page, PageHead, PageLead } from "../components/Page";
import { Refused, refusalOf, type Refusal } from "../components/Refused";
import type { MenuItem } from "../components/RowMenu";
import { EmptyRows, Grouped } from "../components/Rows";
import { TerminalCommands } from "../components/TerminalCommands";
import { download } from "../download";
import { navigate } from "../router";
import { t } from "../i18n";
import { AuthorCommands } from "../components/CommandLine";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** The id Upload a file names in aria-controls, so the dialog gives the focus back to it as it closes. */
const uploadId = "file-upload";

/**
 * Putting a file from the browser: a drop zone with, inside it, the button
 * that opens the computer's file chooser, then a folder's line from a
 * terminal. The upload dialog shows it, and so does My files while it is
 * empty, `lead` then said first in the zone (byj-1wv.51.25). What it puts is
 * the page's (`onDrop`, `onChoose`), so a refusal is said on the page; a file
 * dropped on the zone is its own, not the page's under it. `lit` is a file
 * dragged over the page, which the zone then says in place of the page's
 * overlay, so it is not hidden under that.
 */
function FileUpload({ busy, onChoose, onDrop, chooseRef, lead, lit = false }: {
  busy: string | null;
  onChoose: () => void;
  onDrop: (e: DragEvent) => void;
  chooseRef?: RefObject<HTMLButtonElement>;
  lead?: string;
  lit?: boolean;
}) {
  const [over, setOver] = useState(false);
  const dropHere = busy ? t("files:fileUploading", { name: busy }) : t("files:fileDropHere");
  return (
    <>
      <div
        className={`file-drop${over || lit ? " over" : ""}`}
        data-testid="file-upload-drop"
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // while one is being put, none is taken: the zone lights nothing (byj-1wv.70.8)
          if (busy) {
            if (e.dataTransfer) e.dataTransfer.dropEffect = "none";
          } else setOver(true);
        }}
        // moving onto its own icon, words or button is not leaving it, as the page's leave is
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
        }}
        onDrop={(e) => {
          e.stopPropagation();
          setOver(false);
          onDrop(e);
        }}
      >
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
        </svg>
        {lead && (
          <p className="flush">
            <strong>{lead}</strong>
          </p>
        )}
        <p className="flush">{lead ? dropHere : <strong>{dropHere}</strong>}</p>
        <p className="muted small flush">{t("files:fileDropLimit")}</p>
        <button ref={chooseRef} type="button" className="small" disabled={busy !== null} onClick={onChoose}>
          {t("files:fileChooseFile")}
        </button>
      </div>
      <p className="muted small flush">{t("files:fileFolderByTerminal")}</p>
      <AuthorCommands commands={[putCommand]} />
    </>
  );
}

/**
 * Uploading a file, a dialog from the head's Upload a file: FileUpload's drop
 * zone — the page takes a file dropped anywhere too — its chooser and a
 * folder's line. Not a FormDialog: nothing is submitted, a file is put the
 * moment it is chosen or dropped. As FormDialog does, it gives the focus back
 * to its opener as it closes.
 */
function UploadDialog({ busy, onChoose, onDrop, onClose }: {
  busy: string | null;
  onChoose: () => void;
  onDrop: (e: DragEvent) => void;
  onClose: () => void;
}) {
  const choose = useRef<HTMLButtonElement>(null);
  const keys = useModalKeys(onClose);
  useEffect(() => {
    const opener = document.querySelector<HTMLElement>(`[aria-controls="${uploadId}"]`);
    choose.current?.focus();
    return () => opener?.focus();
  }, []);
  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div id={uploadId} className="modal file-upload-modal stack" role="dialog" aria-modal="true" aria-labelledby="file-upload-title" onKeyDown={keys}>
        <div className="row">
          <h3 id="file-upload-title" className="grow flush">
            {t("files:fileUpload")}
          </h3>
          <button type="button" className="small" aria-label={t("common:close")} onClick={onClose}>
            ✕
          </button>
        </div>
        <FileUpload busy={busy} onChoose={onChoose} onDrop={onDrop} chooseRef={choose} />
      </div>
    </div>
  );
}


export function Files({ client = api }: { client?: FilesApi }) {
  const [data, setData] = useState<Awaited<ReturnType<FilesApi["files"]>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState<Refusal | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const [ask, confirmDialog] = useConfirm();
  // the file of mine whose name and description are being edited (FileDescribe)
  const [describing, setDescribing] = useState<ShareFile | null>(null);
  const fetch = useCallback(async () => {
    setError(null);
    try {
      setData(await client.files());
    } catch (err) {
      setError(errText(err));
    }
  }, [client]);
  useEffect(() => {
    void fetch();
  }, [fetch]);

  // a file chosen or dropped is put, then opened; one refused for room is said
  // as that. One at a time: a file dropped or chosen while another is being
  // put makes no second file, and the next is taken once it ends (byj-1wv.70.8)
  const put = async (f: File | undefined) => {
    if (!f || busy) return;
    setError(null);
    setRefused(null);
    setBusy(f.name);
    try {
      navigate(`/f/${encodeURIComponent(await putNewFile(client, f))}`);
    } catch (err) {
      const r = refusalOf(err, f.size);
      if (r) setRefused(r);
      else setError(errText(err));
      // what came of it is said on the page, not behind the dialog
      setUploading(false);
    } finally {
      // the next is taken however this one ended, as a file's next version is (FilePage)
      setBusy(null);
    }
  };
  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (busy) return;
    // a folder dropped is no file to read: the page says to put it from a terminal
    const entry = e.dataTransfer.items?.[0]?.webkitGetAsEntry?.();
    if (entry?.isDirectory) {
      setError(t("files:fileFolderByTerminal") + " " + putCommand);
      setUploading(false);
      return;
    }
    void put(e.dataTransfer.files[0]);
  };
  const act = async (f: () => Promise<unknown>) => {
    setError(null);
    try {
      await f();
      await fetch();
    } catch (err) {
      setError(errText(err));
    }
  };
  // as a template's row's: Open, Download, then its owner's; the line that gets it is on its page
  const menu = (f: ShareFile): MenuItem[] => [
    { label: t("files:openFile"), onSelect: () => navigate(`/f/${encodeURIComponent(f.id)}`) },
    ...(f.latest ? [{ label: t("files:fileDownload"), onSelect: () => download(fileContentURL(f.id, "latest", true)) }] : []),
    ...(f.mine
      ? [
          { label: t("files:fileEdit"), onSelect: () => setDescribing(f) },
          {
            label: t("files:fileDelete"),
            danger: true,
            onSelect: async () => {
              if (await ask(t("files:fileDeleteAsk", { name: f.name }), { action: t("files:fileDelete"), danger: true })) await act(() => client.deleteFile(f.id));
            },
          },
        ]
      : []),
  ];
  const pct = data && data.quota > 0 ? Math.min(1, data.used / data.quota) : 1;
  const mine = data?.mine.filter((f) => !f.hand_in) ?? [];
  const handedIn = data?.mine.filter((f) => f.hand_in) ?? [];
  // My files is then the drop zone, which says a file dragged over the page itself
  const noneMine = data !== null && mine.length === 0;

  return (
    <Page
      className={`files-page${over ? " drop-over" : ""}`}
      data-testid="file-drop"
      onDragOver={(e) => {
        // cancelled even while one is being put, so the browser never opens a file dropped then in the page's place
        e.preventDefault();
        if (busy) {
          if (e.dataTransfer) e.dataTransfer.dropEffect = "none";
        } else setOver(true);
      }}
      onDragLeave={(e) => {
        // leaving for a child of the page is not leaving the page
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
      }}
      onDrop={drop}
    >
      <PageHead
        title={t("files:filesTitle")}
        meta={
          data && (
            <span className="stack tight files-used" data-testid="files-used">
              <span className={`small ${data.used >= data.quota ? "error" : "muted"}`}>{t("files:filesUsed", { used: fileBytes(data.used), quota: fileBytes(data.quota) })}</span>
              <Meter label={t("files:filesUsed", { used: fileBytes(data.used), quota: fileBytes(data.quota) })} used={pct} full={data.used >= data.quota * nearlyFull} />
            </span>
          )
        }
        actions={
          <>
            <TerminalCommands />
            <button
              className="primary small"
              aria-haspopup="dialog"
              aria-expanded={uploading}
              aria-controls={uploading ? uploadId : undefined}
              disabled={busy !== null}
              onClick={() => setUploading(true)}
            >
              {busy ? t("files:fileUploading", { name: busy }) : t("files:fileUpload")}
            </button>
            <input
              ref={input}
              type="file"
              hidden
              data-testid="file-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                // cleared the moment it gives a file, taken or not, so the same file chosen again is a change (byj-1wv.70.8)
                e.target.value = "";
                void put(f);
              }}
            />
          </>
        }
      />
      <PageLead>{t("files:filesLead")}</PageLead>
      {data && data.used > 0 && data.used >= data.quota * nearlyFull && !refused && (
        <p className="warn small" data-testid="files-nearly-full">
          {t("files:filesNearlyFull", { pct: data.quota > 0 ? Math.min(100, Math.round((data.used / data.quota) * 100)) : 100 })}
        </p>
      )}
      {refused && <Refused r={refused} mine={data?.mine} />}
      {error && (
        <p className="error">
          {t("common:error", { m: error })} <button className="small" onClick={() => void fetch()}>{t("common:retry")}</button>
        </p>
      )}
      {over && !noneMine && (
        <div className="file-drop-over" aria-hidden="true">
          <p>{t("files:fileDropOver")}</p>
        </div>
      )}
      {data === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : (
        <div className="rows-panel">
          <Grouped
            id="files"
            headed
            row={(f) => <FileRow key={f.id} f={f} menu={menu(f)} />}
            groups={[
              {
                title: t("files:filesMine"),
                items: mine,
                testid: "files-mine",
                none: (
                  // the upload dialog's own, so what puts a file is in the empty card itself
                  <div className="stack file-empty" data-testid="no-files-mine">
                    <FileUpload busy={busy} onChoose={() => input.current?.click()} onDrop={drop} lead={t("files:noFilesMine")} lit={over} />
                  </div>
                ),
              },
              // what goes with its class's retention, said once over its rows (RFC-054 §5)
              { title: t("files:filesHandedIn"), items: handedIn, testid: "files-handed-in", note: <p className="muted small panel-lead">{t("files:filesHandedInNote")}</p> },
              {
                title: t("files:filesShared"),
                items: data.shared,
                testid: "files-shared",
                none: (
                  <div data-testid="no-files-shared">
                    <EmptyRows text={t("files:noFilesShared")} />
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}
      {uploading && <UploadDialog busy={busy} onChoose={() => input.current?.click()} onDrop={drop} onClose={() => setUploading(false)} />}
      {describing && (
        <FileDescribe
          file={describing}
          save={async (patch) => {
            await client.describeFile(describing.id, patch);
            await fetch();
          }}
          onClose={() => setDescribing(null)}
        />
      )}
      {confirmDialog}
    </Page>
  );
}
